import { useMemo } from "react";
import { addYears, startOfDay, endOfDay } from "date-fns";
import { useToken } from "@rentablo/api";
import { useCurrencyDisplay } from "@providers";
import { ExtendedAccount } from "@features/depots/types";
import {
  AccountPerformance,
  isCashLikeAccount,
  lastBalanceValue,
  lastPerformanceValue,
  projectBalances,
  projectPerformanceStatistic,
} from "./helpers";
import { buildSpecs, isAnyFetching } from "./querySpecs";
import { useBalanceQueries } from "./useBalanceQueries";
import { usePerformanceStatisticQueries } from "./usePerformanceStatisticQueries";

export type { AccountPerformance } from "./helpers";

export const useAccountsPerformance = (
  accounts: ExtendedAccount[]
): Record<number, AccountPerformance> => {
  const { data: token } = useToken();
  const { globalCurrency } = useCurrencyDisplay();
  const startDate = startOfDay(addYears(new Date(), -1));
  const endDate = endOfDay(new Date());
  const accessToken = token?.access_token;

  const { performanceSpecs, balanceSpecs } = useMemo(() => {
    const cashLikeAccounts = accounts.filter(isCashLikeAccount);
    const otherAccounts = accounts.filter(
      (account) => !isCashLikeAccount(account)
    );
    return {
      performanceSpecs: buildSpecs(otherAccounts, globalCurrency),
      balanceSpecs: buildSpecs(cashLikeAccounts, globalCurrency),
    };
  }, [accounts, globalCurrency]);

  const performanceByAccount = usePerformanceStatisticQueries(
    performanceSpecs,
    { startDate, endDate },
    accessToken
  );
  const balancesByAccount = useBalanceQueries(balanceSpecs, accessToken);

  return accounts.reduce<Record<number, AccountPerformance>>((acc, account) => {
    if (isCashLikeAccount(account)) {
      const { global, account: native } = balancesByAccount[account.id] ?? {};
      acc[account.id] = {
        ...projectBalances(global?.data, startDate, endDate),
        nativePortfolioValue: native
          ? lastBalanceValue(native.data)
          : undefined,
        isFetching: isAnyFetching(global, native),
      };
      return acc;
    }

    const { global, account: native } = performanceByAccount[account.id] ?? {};
    acc[account.id] = {
      ...projectPerformanceStatistic(global?.data),
      nativePortfolioValue: native
        ? lastPerformanceValue(native.data)
        : undefined,
      isFetching: isAnyFetching(global, native),
    };
    return acc;
  }, {});
};
